import { defineStore } from 'pinia'
import { computed, ref, Ref } from 'vue'
import { useSessionStore } from './session'

import { IPortalMessage, PortalMessageType } from '@/types/portalMessage';
import { DEBUG_WARN, NotificationApi, StringUtil } from '@/util';

const refreshInterval = 5 /* minutes */ * 60000 /* ms */

/** store of localized units of measurement */
export const usePortalMessageStore = defineStore('portalMessageStore', () => {
    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _messages: Ref<IPortalMessage[]> = ref([]);
    const _hiddenNotification: Ref<string> = ref('')
    const _hiddenWarning: Ref<string> = ref('')

    const state = { _messages, _hiddenNotification, _hiddenWarning }

    /********************************************************************************************************************
     GETTERS
    ********************************************************************************************************************/
    const notification = computed(() => _messages.value
        .find(msg => msg.type == PortalMessageType.Notification && msg.text != _hiddenNotification.value)
        ?.text
    );

    const warning = computed(() => _messages.value
        .find(msg => msg.type == PortalMessageType.Warning && msg.text != _hiddenWarning.value)
        ?.text
    );

    const maintenanceMessage = computed(() => _messages.value
        .find(msg => msg.type == PortalMessageType.Maintenance)
        ?.text
    );

    const getters = { notification, warning, maintenanceMessage }

    /********************************************************************************************************************
     ACTIONS
    ********************************************************************************************************************/
    const api = new NotificationApi

    function hideNotification() {
        _hiddenNotification.value = notification.value ?? ''
    }

    function hideWarning() {
        _hiddenWarning.value = warning.value ?? ''
    }

    function refresh() {
        api.getMessages()
            .then(response => setNewMessages(response.data))
            .catch(error => DEBUG_WARN('PortalMessageStore: Failed to fetch portal messages:', error))
    }

    const actions = { hideNotification, hideWarning, refresh }

    /********************************************************************************************************************
     PRIVATE
    ********************************************************************************************************************/
    const sessionStore = useSessionStore()
    function setNewMessages(newMessages: IPortalMessage[]) {
        _messages.value = newMessages

        if (StringUtil.hasContent(maintenanceMessage.value)) {
            // If a maintenance message is present, log off any current session
            sessionStore.isAuthenticated().then(isAuthenticated => {
                if (isAuthenticated) {
                    sessionStore.logOff()
                }
            })
        }
    }

    /********************************************************************************************************************
     INIT
     ********************************************************************************************************************/
    refresh()
    setInterval(() => refresh(), refreshInterval)

    return { ...state, ...getters, ...actions }
})